import React, { useEffect, useState } from "react";
import { FaqInterface } from "../../interfaces/faq.interface";
import { get, remove } from "../../redux/ApiHelper";
import { successToast, errorToast } from "../../utils/toast.util";
import { MdAdd, MdEdit, MdDeleteOutline } from "react-icons/md";
import FaqModal from "./FaqModal";

const truncate = (text: string, max = 60) =>
  text.length > max ? `${text.slice(0, max)}…` : text;

const FaqSettings = () => {
  const [faqs, setFaqs] = useState<FaqInterface[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editData, setEditData] = useState<FaqInterface | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchFaqs = async () => {
    try {
      setIsLoading(true);
      const response = await get("/admin/faq/all");
      setFaqs(response?.data || []);
    } catch (error) {
      errorToast("Failed to fetch FAQs");
      console.error("Error fetching FAQs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  const handleDelete = async (faqId: string) => {
    if (!window.confirm("Are you sure you want to delete this FAQ?")) {
      return;
    }

    try {
      setIsLoading(true);
      await remove(`/admin/faq/delete/${faqId}`);
      successToast("FAQ deleted successfully");
      fetchFaqs();
    } catch (error) {
      errorToast("Failed to delete FAQ");
      console.error("Error deleting FAQ:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = () => {
    setIsEdit(false);
    setEditData(null);
    setIsModalOpen(true);
  };

  const handleEdit = (faq: FaqInterface) => {
    setIsEdit(true);
    setEditData(faq);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditData(null);
    setIsEdit(false);
  };

  const handleModalSuccess = () => {
    fetchFaqs();
    closeModal();
  };

  return (
    <div className="bg-gray-900 text-yellow-500 flex items-center justify-center p-4 flex-grow min-h-screen">
      <div className="bg-gray-800 p-6 rounded-lg shadow-lg w-full max-w-6xl">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-xl font-semibold text-yellow-500">
            FAQ Settings
          </h1>
          <button
            onClick={openModal}
            className="bg-yellow-500 text-gray-900 px-4 py-2 rounded-md hover:bg-yellow-400 transition font-medium flex items-center gap-2"
          >
            <MdAdd className="text-xl" />
            Add New FAQ
          </button>
        </div>

        {isLoading && faqs.length === 0 ? (
          <div className="text-center py-12 text-gray-400">Loading FAQs...</div>
        ) : faqs.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            No FAQs found. Click &quot;Add New FAQ&quot; to create one.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-700 border-b-2 border-yellow-500">
                  <th className="px-6 py-4 text-left text-yellow-500 font-semibold">
                    For Whom
                  </th>
                  <th className="px-6 py-4 text-left text-yellow-500 font-semibold">
                    Question
                  </th>
                  <th className="px-6 py-4 text-left text-yellow-500 font-semibold">
                    Order
                  </th>
                  <th className="px-6 py-4 text-left text-yellow-500 font-semibold">
                    Status
                  </th>
                  <th className="px-6 py-4 text-center text-yellow-500 font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {faqs.map((faq, index) => (
                  <tr
                    key={faq._id || index}
                    className={`border-b border-gray-700 hover:bg-gray-700/50 transition ${
                      index % 2 === 0 ? "bg-gray-800" : "bg-gray-800/50"
                    }`}
                  >
                    <td className="px-6 py-4 text-gray-300 font-medium">
                      {faq.forWhom}
                    </td>
                    <td
                      className="px-6 py-4 text-gray-300 font-medium max-w-xs"
                      title={faq.question}
                    >
                      {truncate(faq.question)}
                    </td>
                    <td className="px-6 py-4 text-gray-300 font-medium">
                      {faq.order ?? 0}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`text-sm font-medium ${
                          faq.isActive !== false
                            ? "text-green-400"
                            : "text-red-400"
                        }`}
                      >
                        {faq.isActive !== false ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex justify-center items-center gap-3">
                        <button
                          onClick={() => handleEdit(faq)}
                          className="p-2 rounded-full bg-yellow-500/20 text-yellow-500 hover:bg-yellow-500/30 transition cursor-pointer"
                          title="Edit FAQ"
                        >
                          <MdEdit className="text-lg" />
                        </button>
                        <button
                          onClick={() => faq._id && handleDelete(faq._id)}
                          className="p-2 rounded-full bg-red-500/20 text-red-500 hover:bg-red-500/30 transition cursor-pointer"
                          title="Delete FAQ"
                        >
                          <MdDeleteOutline className="text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="mt-4 text-sm text-gray-400 text-right">
              Total FAQs: {faqs.length}
            </div>
          </div>
        )}

        <FaqModal
          isOpen={isModalOpen}
          closeModal={closeModal}
          isEdit={isEdit}
          editData={editData}
          onSuccess={handleModalSuccess}
        />
      </div>
    </div>
  );
};

export default FaqSettings;
